import { ReactNode } from "react";
import { usePublicData } from "../hooks/usePublicData";
import { fetchPilot } from "../services/pilotsService";
import { fetchAchievements } from "../services/achievementsService";
import { fetchFeed } from "../services/feedService";
import NextUnlocks from "./NextUnlocks";
import { ACHIEVEMENT_ICONS, DEFAULT_ACHIEVEMENT_ICON } from "../constants/achievementIcons";

// Next unlocks rows are about twice as tall as an award row, so the award columns list more to line up.
const PROFICIENCY_ROWS = 4;
const ACHIEVEMENT_ROWS = 6;

function AwardDate({ date }: { date: string }) {
  return <time dateTime={date}>{new Date(date).toLocaleDateString("en-AU", { timeZone: "Australia/Melbourne" })}</time>;
}

function Column({ title, href, aside, children }: { title: string; href?: string; aside?: ReactNode; children: ReactNode }) {
  return <div>
    <div className="dashboard-widget-header dashboard-widget-header-sub">
      <h3>{title}</h3>
      {aside ?? (href ? <a href={href}>View all</a> : null)}
    </div>
    {children}
  </div>;
}

export default function PilotSummary({ uid }: { uid: string }) {
  const identity = usePublicData(`pilot:${uid}`, () => fetchPilot(uid));
  const base = `#/pilots/${encodeURIComponent(uid)}`;
  return <section className="panel pinned-pilot">
    <div className="dashboard-widget-header">
      <div className="pinned-title">
        <span className="pinned-kicker">At a glance</span>
        <div className="pinned-name-row">
          <h2>{identity.data ? (identity.data.name || "Unnamed pilot") : (identity.loading ? "Loading pilot…" : "Pilot unavailable")}</h2>
        </div>
      </div>
      <div className="pinned-actions">
        {identity.data && <a href={base}>Full profile →</a>}
      </div>
    </div>
    {identity.error && <div role="alert" className="dashboard-empty-row">{identity.error.includes("404") ? "This pilot no longer exists. Use the pilot menu to choose another or unpin." : "Unable to load the pilot. Your selection is still saved."} <button onClick={identity.refresh}>Retry</button></div>}
    {identity.data && <PilotAwards key={uid} uid={uid} />}
  </section>;
}

function PilotAwards({ uid }: { uid: string }) {
  const achievements = usePublicData(`achievements:${uid}`, () => fetchAchievements(uid));
  const earned = usePublicData(`earned:${uid}`, () => fetchFeed({ pilot_uid: uid, kind: "proficiency", limit: PROFICIENCY_ROWS }));
  const recent = [...(achievements.data?.achievements ?? [])].filter(a => a.earned_at).sort((a, b) => (b.earned_at || "").localeCompare(a.earned_at || "") || a.id.localeCompare(b.id)).slice(0, ACHIEVEMENT_ROWS);

  return <div className="pinned-columns">
    <Column title="Next unlocks" href={`#/proficiencies?pilot=${encodeURIComponent(uid)}`}>
      <NextUnlocks pilotUid={uid} heading={false} limit={3} emptyMessage="No proficiency progress yet." />
    </Column>
    <Column title="Recent proficiencies" href={`#/proficiencies?pilot=${encodeURIComponent(uid)}`}>
      {earned.loading ? <p role="status" className="dashboard-empty-row">Loading earned proficiencies…</p> : earned.error ? <div role="alert" className="dashboard-empty-row">Earned proficiencies are unavailable. <button onClick={earned.refresh}>Retry earned proficiencies</button></div> :
        !earned.data?.items.length ? <p className="dashboard-empty-row">No earned proficiencies yet.</p> : <ul className="dashboard-achievement-list pinned-awards">{earned.data.items.map(item => <li className="dashboard-achievement-row" key={item.id}>
          <div className="dashboard-proficiency-head"><div className="dashboard-achievement-title">{item.metadata.proficiency_title || item.title}</div><AwardDate date={item.occurred_at} /></div>
          <div className="dashboard-achievement-meta"><span>{String(item.summary_vars?.level_display || item.metadata.level_reached || "")}</span></div>
        </li>)}</ul>}
    </Column>
    <Column title="Recent achievements" href={`#/achievements?pilot=${encodeURIComponent(uid)}`}>
      {achievements.loading ? <p role="status" className="dashboard-empty-row">Loading achievements…</p> : achievements.error ? <div role="alert" className="dashboard-empty-row">Achievements are unavailable. <button onClick={achievements.refresh}>Retry achievements</button></div> :
        !recent.length ? <p className="dashboard-empty-row">No achievements earned yet.</p> : <ul className="dashboard-achievement-list pinned-awards">{recent.map(a => <li className="dashboard-achievement-row" key={a.id}>
          <div className="dashboard-proficiency-head"><div className="dashboard-achievement-title"><span className="dashboard-achievement-icon" aria-hidden="true">{ACHIEVEMENT_ICONS[a.id] || DEFAULT_ACHIEVEMENT_ICON}</span>{a.name}</div><AwardDate date={a.earned_at!} /></div>
        </li>)}</ul>}
    </Column>
  </div>;
}
